import { useEffect, useRef, useState } from 'react';
import { FaPhone, FaPhoneSlash, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { useCall } from '../contexts/CallContext';
import { Avatar } from './Avatar';

function useElapsedSeconds(active) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) { setSeconds(0); return; }
    const start = Date.now();
    const id = setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  return seconds;
}

function formatDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// A short repeating two-tone ring via WebAudio -- no audio asset needed.
function useRingtone(active) {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;

    const ring = () => {
      [880, 660].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        gain.gain.value = 0.15;
        osc.connect(gain).connect(ctx.destination);
        const start = ctx.currentTime + i * 0.25;
        osc.start(start);
        osc.stop(start + 0.2);
      });
    };
    ring();
    intervalRef.current = setInterval(ring, 1500);

    return () => {
      clearInterval(intervalRef.current);
      ctx.close();
    };
  }, [active]);
}

export default function CallOverlay() {
  const { status, peer, muted, error, acceptCall, declineCall, endCall, toggleMute, dismissError, remoteAudioRef } = useCall();
  const elapsed = useElapsedSeconds(status === 'active');
  useRingtone(status === 'ringing-incoming');

  if (status === 'idle') return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <audio ref={remoteAudioRef} autoPlay />

      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center">
        <Avatar id={peer?.id} name={peer?.name || '?'} size={72} />
        <p className="mt-4 text-lg font-semibold text-[#0c1b4d]">{peer?.name}</p>
        <p className="mt-1 text-sm text-gray-400">
          {status === 'ringing-outgoing' && 'Calling…'}
          {status === 'ringing-incoming' && 'Incoming call…'}
          {status === 'active' && formatDuration(elapsed)}
          {status === 'error' && 'Call ended'}
        </p>
        {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

        {status === 'error' ? (
          <div className="mt-8">
            <button
              onClick={dismissError}
              className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Dismiss
            </button>
          </div>
        ) : (
          <div className="mt-8 flex items-center justify-center gap-4">
            {status === 'ringing-incoming' && (
              <button
                onClick={acceptCall}
                className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-colors shadow-lg"
                title="Accept"
              >
                <FaPhone size={20} />
              </button>
            )}
            {status === 'active' && (
              <button
                onClick={toggleMute}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors shadow-lg ${muted ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-[#0c1b4d]'}`}
                title={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <FaMicrophoneSlash size={18} /> : <FaMicrophone size={18} />}
              </button>
            )}
            <button
              onClick={status === 'ringing-incoming' ? declineCall : endCall}
              className="w-14 h-14 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
              title={status === 'ringing-incoming' ? 'Decline' : 'Hang up'}
            >
              <FaPhoneSlash size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
